import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';

interface GeoJSONLayerProps {
  data: any; // GeoJSON FeatureCollection
  color: string;
  visible: boolean;
  onFeatureClick?: (feature: any) => void;
}

export function GeoJSONLayer({ data, color, visible, onFeatureClick }: GeoJSONLayerProps) {
  const map = useMap();
  const layerRef = useRef<L.GeoJSON | null>(null);
  const dataRef = useRef(data);
  const colorRef = useRef(color);

  // Create layer once and only once when data is first available
  useEffect(() => {
    if (!map || !data || layerRef.current) return;

    // Store refs to avoid recreating layer
    dataRef.current = data;
    colorRef.current = color;

    // Create new GeoJSON layer with native Leaflet rendering
    const geoJsonLayer = L.geoJSON(data, {
      pointToLayer: (feature, latlng) => {
        // Use CircleMarker for better performance
        return L.circleMarker(latlng, {
          radius: 5,
          fillColor: color,
          color: color,
          weight: 0,
          opacity: 0,
          fillOpacity: 1,
          // Add pane to ensure proper z-index handling
          pane: 'markerPane',
        });
      },
      onEachFeature: (feature, layer) => {
        // Add hover effects
        layer.on('mouseover', function (e) {
          const marker = e.target;
          marker.setStyle({
            radius: 8,
            weight: 3,
            opacity: 1,
            color: '#ffffff',
            fillOpacity: 1,
          });
          marker.bringToFront();
        });

        layer.on('mouseout', function (e) {
          const marker = e.target;
          marker.setStyle({
            radius: 5,
            weight: 0,
            opacity: 0,
            fillOpacity: 1,
          });
        });

        // Bind popup on hover and click
        if (feature.properties) {
          const props = feature.properties;
          const schoolName = props.name || props.schoolTitle || 'Unknown School';
          const schoolId = props.id || props.schoolName || 'N/A';
          
          const popupContent = `
            <div class="school-card">
              <div class="school-card-header">
                <div class="school-card-info">
                  <h3>${schoolName}</h3>
                  <p class="school-id">${schoolId}</p>
                  <span class="status-badge" style="background-color: ${color};">
                    ${(props.status || 'unknown').toUpperCase()}
                  </span>
                </div>
              </div>
            </div>
          `;
          
          const popup = L.popup({
            closeButton: false,
            maxWidth: 280,
            className: 'school-popup-custom',
            autoPan: true,
            offset: [0, -5],
            autoClose: true,
            closeOnClick: true,
          }).setContent(popupContent);

          layer.bindPopup(popup);
          
          // Open popup on hover
          layer.on('mouseover', function (this: L.Layer) {
            this.openPopup();
          });
          
          // Close popup when mouse leaves
          layer.on('mouseout', function (this: L.Layer) {
            this.closePopup();
          });
          
          // Keep popup open on click and allow navigation
          layer.on('click', function (this: L.Layer, e: L.LeafletMouseEvent) {
            L.DomEvent.stopPropagation(e);
            this.openPopup();
            if (onFeatureClick) {
              onFeatureClick(feature);
            }
          });
        }
      },
    });

    layerRef.current = geoJsonLayer;

    // Cleanup on unmount only
    return () => {
      if (layerRef.current && map) {
        if (map.hasLayer(layerRef.current)) {
          map.removeLayer(layerRef.current);
        }
        layerRef.current = null;
      }
    };
  }, [map, data]); // Only depend on map and data, not color or onFeatureClick

  // Handle visibility changes without recreating the layer
  useEffect(() => {
    if (!layerRef.current || !map) return;

    if (visible) {
      if (!map.hasLayer(layerRef.current)) {
        layerRef.current.addTo(map);
      }
    } else {
      if (map.hasLayer(layerRef.current)) {
        map.removeLayer(layerRef.current);
      }
    }
  }, [visible, map]);

  return null;
}

